import { type AvatarGroupProps } from '@atlaskit/avatar-group';
import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';

export type CrossJoinSourceProduct =
	| 'confluence'
	| 'jira-software'
	| 'jira-product-discovery'
	| 'jira-core'
	| 'jira-servicedesk'
	| 'jira-customer-service';

export type CrossJoinTargetProduct =
	| 'jira-software'
	| 'jira-product-discovery'
	| 'confluence'
	| 'loom';

export type CrossJoinIntegration =
	| 'crossJoinNudgesV3'
	| 'crossJoinNudgesConfluenceToJpd'
	| 'crossJoinNudgesJiraToConfluence'
	| 'crossJoinNudgesJiraToJpd'
	| 'crossJoinNudgesConfluenceToJpdMsr'
	| 'crossJoinNudgesConfluenceToJiraMsr'
	| 'crossJoinNudgesConfluenceToLoom'
	| 'invitedAppShortcuts';

/**
 * @deprecated Use CrossJoinIntegration instead. This was a typo and will be removed in a future version.
 */
export type CrossJoinIntegraton = CrossJoinIntegration;

export type Collaborator = {
	avatar: string;
	id: string;
	name: string;
};

export type Collaborators = Collaborator[];

// manage the allowed apps and their keys
export const INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS = ['jira', 'confluence'] as const;
export type InvitedAppShortcutAllowedTargetAppKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS)[number];
export const INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS = ['confluence', 'jira', 'home'] as const;
export type InvitedAppShortcutAllowedSourceAppKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS)[number];
export const INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS = ['JIRA_SOFTWARE', 'CONFLUENCE'] as const;
export type InvitedAppShortcutAllowedApsKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS)[number];

/**
 * Lozenge variant for an Invited App Shortcut, parsed out of the GRS
 * recommendation `entityId` by `parseInvitedAppShortcutEntityId`.
 *
 * `none` is the safe fallback and means "render the shortcut, but no lozenge".
 * It is what a plain product `entityId` (e.g. `jira`) resolves to, and what an
 * unrecognised variant suffix degrades to, so an unknown future variant from
 * GRS can never make a shortcut disappear.
 */
export const INVITED_APP_SHORTCUT_VARIANTS = ['animated', 'static', 'none'] as const;
export type InvitedAppShortcutVariant = (typeof INVITED_APP_SHORTCUT_VARIANTS)[number];

/**
 * A recommended target app together with the lozenge variant parsed from its
 * GRS `entityId`.
 */
export type InvitedAppShortcutRecommendedApp = {
	product: InvitedAppShortcutAllowedTargetAppKey;
	variant: InvitedAppShortcutVariant;
};

/**
 * Accepted element type for `recommendedApps`.
 *
 * The bare `InvitedAppShortcutAllowedTargetAppKey` member exists for backward
 * compatibility: consumers that have not migrated to variant-aware
 * recommendations keep passing plain product keys, which normalise to
 * `variant: 'none'`. Use `normaliseRecommendedApp` to collapse either form to
 * `InvitedAppShortcutRecommendedApp`.
 *
 * The bare member is scheduled for removal once Jira and Confluence both pass
 * the object form (PUMA-2247 PR 4).
 */
export type InvitedAppShortcutRecommendedAppInput =
	| InvitedAppShortcutAllowedTargetAppKey
	| InvitedAppShortcutRecommendedApp;

export type ShortcutItemsConfig = Partial<
	Record<InvitedAppShortcutAllowedTargetAppKey, GlobalShortcutsItem>
>;

export type InvitedAppShortcutCollaborator = Pick<
	AvatarGroupProps['data'][number],
	'name' | 'src' | 'key'
>;
